/**
 * newsletter-drawer.js — Newsletter signup drawer Web Component.
 *
 * Sibling of the search drawer: a right-side slide-in panel holding a
 * newsletter signup form. Opened via the global event bus; closed by the
 * close button, Escape, or a backdrop click.
 *
 * Events (via TRYEvents):
 *   Listens: 'newsletter:open', 'newsletter:close'
 *
 * Submission currently goes to Shopify's native newsletter signup
 * ({% form 'customer' %}). See #subscribe for the Voyado Engage seam.
 */
class NewsletterDrawer extends TRYComponent {
  /** @type {FocusTrap|null} */
  #trap = null;

  /** Aborts document-level listeners (open triggers) on disconnect. */
  #controller = null;

  #form;
  #email;
  #submitBtn;
  #successEl;
  #errorEl;

  /** Bound keydown handler — stored so it can be removed on disconnect. */
  #onKeydown = (e) => {
    if (e.key === 'Escape' && this.classList.contains('is-open')) this.close();
  };

  connectedCallback() {
    super.connectedCallback();

    this.#form = this.getRequired('.newsletter-drawer__form');
    this.#email = this.getRequired('[name="contact[email]"]');
    this.#submitBtn = this.getRequired('[data-newsletter-submit]');
    this.#successEl = this.getRequired('[data-newsletter-success]');
    this.#errorEl = this.getRequired('[data-newsletter-error]');

    this.addEventListener('click', this.#onClick.bind(this));
    this.#form.addEventListener('submit', this.#onSubmit.bind(this));
    document.addEventListener('keydown', this.#onKeydown);

    TRYEvents.on('newsletter:open', () => this.open());
    TRYEvents.on('newsletter:close', () => this.close());

    // Open from any [data-newsletter-open] trigger (e.g. the footer button)
    // without inline handlers. Delegated on document so triggers anywhere work.
    this.#controller = new AbortController();
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-newsletter-open]')) {
        e.preventDefault();
        this.open();
      }
    }, { signal: this.#controller.signal });

    // Native (no-JS) submit reloads with the success/error state pre-rendered by
    // Shopify (server-scoped, inside the form) — open the drawer so the visitor
    // sees the confirmation. The status elements' [hidden] is our signal.
    if (!this.#successEl.hidden) {
      this.#showSuccess();
      this.open();
    } else if (!this.#errorEl.hidden) {
      this.open();
    }
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#trap?.release({ restoreFocus: false });
    document.removeEventListener('keydown', this.#onKeydown);
  }

  open() {
    const wasOpen = this.classList.contains('is-open');
    this.classList.add('is-open');
    this.removeAttribute('aria-hidden');
    if (!wasOpen) {
      this.#trap = new FocusTrap(this);
      this.#trap.activate(this.#email);
    }
  }

  close() {
    this.classList.remove('is-open');
    this.#trap?.release();
    this.#trap = null;
    this.setAttribute('aria-hidden', 'true');
  }

  /** @param {MouseEvent} e */
  #onClick(e) {
    // Close button, or a click on the backdrop (anywhere outside the panel).
    if (e.target.closest('[data-close]') || !e.target.closest('.newsletter-drawer__panel')) {
      this.close();
    }
  }

  /** @param {SubmitEvent} e */
  async #onSubmit(e) {
    e.preventDefault();

    this.#errorEl.hidden = true;

    if (!this.#email.value.trim() || !this.#email.checkValidity()) {
      this.#showError(this.dataset.msgInvalid);
      this.#email.focus();
      return;
    }

    this.#submitBtn.setAttribute('aria-busy', 'true');

    try {
      await this.#subscribe();
      this.#showSuccess();
    } catch (err) {
      console.error('[NewsletterDrawer] signup error:', err);
      this.#showError(this.dataset.msgError);
    } finally {
      this.#submitBtn.removeAttribute('aria-busy');
    }
  }

  /**
   * Subscribe the entered email.
   *
   * === Voyado Engage seam ===
   * This is the single place a Voyado Engage newsletter call should be added
   * as the PRIMARY path (falling back to the Shopify submit below on failure).
   * No Engage credentials are wired yet, so Shopify is the active path.
   *
   * Posts the Shopify customer form. On success Shopify redirects to
   * `?customer_posted=true`; fetch follows it, so a redirected response whose
   * URL carries that flag is our success signal. (Malformed emails are caught
   * client-side before we get here.)
   *
   * @throws if the signup did not succeed.
   */
  async #subscribe() {
    const resp = await fetch(this.#form.action, {
      method: 'POST',
      body: new FormData(this.#form),
      headers: { Accept: 'text/html' },
    });

    const success = resp.redirected
      ? resp.url.includes('customer_posted=true')
      : resp.ok;

    if (!success) throw new Error(`Newsletter signup failed: ${resp.status}`);
  }

  #showSuccess() {
    this.#errorEl.hidden = true;
    this.#successEl.hidden = false;
    this.#email.value = '';
    this.#email.closest('.newsletter-drawer__field').hidden = true;
    this.#submitBtn.hidden = true;
  }

  /** @param {string} message */
  #showError(message) {
    this.#errorEl.textContent = message;
    this.#errorEl.hidden = false;
  }
}

customElements.define('newsletter-drawer', NewsletterDrawer);
